/*
 *  Copyright (c) 2023 NOOGA DEVELOPMENT, All rights reserved.
 *  Author Rihard Tsybulka <rykhard.tsybulka@noogadev.com>
 */

import { Epic, ofType } from 'redux-observable';
import { RootState } from '../store.typings';
import { catchError, map, switchMap, takeUntil } from 'rxjs/operators';
import { from, of } from 'rxjs';
import { documentsClient } from '../api/documents/documentsClient';
import { AxiosError } from 'axios';
import { parseError } from '../../utils/utils';
import { i18n } from '../translation/i18n';
import {
    DocumentDetailsAction,
    DocumentDetailsActionType,
    fetchDocumentCardDetailsSuccessAction,
    fetchDocumentCardDetailsErrorAction,
    fetchDocumentItemsAiCategoriesSuccessAction,
    fetchDocumentItemsAiCategoriesErrorAction,
    fetchCashbackInfoSuccessAction,
    fetchCashbackInfoErrorAction,
    fetchVatVerificationInfoSuccessAction,
    fetchVatVerificationInfoErrorAction,
} from '../actions/documentDetailsActions';
import { cashbackClient } from '../api/cashback/cashbackClient';

type DocumentDetailsEpic = Epic<DocumentDetailsAction, DocumentDetailsAction, RootState>;

export const fetchDocumentCardDetailsEpic: DocumentDetailsEpic = (action$, state$) => {
    return action$.pipe(
        ofType(DocumentDetailsActionType.OPEN_REGISTRY_DOCUMENT_CARD_PAGE),
        switchMap(() => {
            const { doc } = state$.value.routerState.route.params;

            return from(documentsClient.getDocumentCard(doc)).pipe(
                map((data) => {
                    return fetchDocumentCardDetailsSuccessAction(data);
                }),

                catchError((err: AxiosError) =>
                    of(
                        fetchDocumentCardDetailsErrorAction(
                            parseError(err, i18n.t('RegistryScreens.errors.failedToGetDocument')),
                        ),
                    ),
                ),
                takeUntil(action$.ofType(DocumentDetailsActionType.EXIT_REGISTRY_DOCUMENT_CARD_PAGE)),
            );
        }),
    );
};

export const fetchProductItemsAiCategoriesEpic: DocumentDetailsEpic = (action$, state$) => {
    return action$.pipe(
        ofType(DocumentDetailsActionType.FETCH_DOCUMENT_ITEMS_AI_CATEGORIES_START),
        switchMap(() => {
            const { doc } = state$.value.routerState.route.params;

            return from(documentsClient.getProductItemsAiCategories(doc)).pipe(
                map((data) => {
                    return fetchDocumentItemsAiCategoriesSuccessAction(data);
                }),

                catchError((err: AxiosError) =>
                    of(
                        fetchDocumentItemsAiCategoriesErrorAction(
                            parseError(err, i18n.t('RegistryScreens.errors.failedToGetProductItemsAiCategories')),
                        ),
                    ),
                ),
                takeUntil(action$.ofType(DocumentDetailsActionType.EXIT_REGISTRY_DOCUMENT_CARD_PAGE)),
            );
        }),
    );
};

export const fetchCashBackInfoEpic: DocumentDetailsEpic = (action$, state$) => {
    return action$.pipe(
        ofType(
            DocumentDetailsActionType.OPEN_REGISTRY_DOCUMENT_CARD_PAGE,
            DocumentDetailsActionType.FETCH_CASHBACK_INFO_START,
        ),
        switchMap(() => {
            const { doc } = state$.value.routerState.route.params;

            return from(cashbackClient.getDocumentCashbackInfo(doc)).pipe(
                map((data) => {
                    return fetchCashbackInfoSuccessAction(data);
                }),

                catchError(() => of(fetchCashbackInfoErrorAction())),
                takeUntil(action$.ofType(DocumentDetailsActionType.EXIT_REGISTRY_DOCUMENT_CARD_PAGE)),
            );
        }),
    );
};

export const fetchVatVerificationInfoEpic: DocumentDetailsEpic = (action$, state$) => {
    return action$.pipe(
        ofType(
            DocumentDetailsActionType.OPEN_REGISTRY_DOCUMENT_CARD_PAGE,
            DocumentDetailsActionType.FETCH_VAT_VERIFICATION_INFO_START,
        ),
        switchMap(() => {
            const { doc } = state$.value.routerState.route.params;

            return from(cashbackClient.getVatVerificationInfo(doc)).pipe(
                map((data) => {
                    return fetchVatVerificationInfoSuccessAction(data);
                }),

                catchError(() => of(fetchVatVerificationInfoErrorAction())),
                takeUntil(action$.ofType(DocumentDetailsActionType.EXIT_REGISTRY_DOCUMENT_CARD_PAGE)),
            );
        }),
    );
};
